import { EnsemblesCollectionResponse, fetchCollectionData } from './collection';
import { itemTeaser } from './ensemble-item';
import { translate, EN, FR, NL } from './translation';

const NUMBER_OF_ARTWORKS_SHOWN_BY_DEFAULT = 12;

class MrEnsemblesCollectionDetailScreen extends HTMLElement {
	#collectionData: EnsemblesCollectionResponse | null = null;

	static get observedAttributes(): Array<string> {
		return [
			'data-collection-slug',
			'data-sort-order',
		];
	}

	get collectionSlug(): string | null {
		return this.getAttribute( 'data-collection-slug' );
	}

	set collectionSlug( value: string | null ) {
		if ( value ) {
			this.setAttribute( 'data-collection-slug', value );

			return;
		}

		this.removeAttribute( 'data-collection-slug' );
	}

	get collectionTitle(): string | null {
		const title = ( JSON.parse( this.getAttribute( 'data-title' ) || '""' ) ?? '' ).trim();
		if ( !title ) {
			return null;
		}

		return title;
	}

	attributeChangedCallback( attrName: string, oldVal: string|null, newVal: string|null ): void {
		if ( 'data-collection-slug' === attrName ) {
			if ( newVal && !document.querySelector( `mr-ensembles-event-modal-dialog#ensembles-event-detail-dialog-${newVal}` ) ) {
				const dialog = document.createElement( 'mr-ensembles-event-modal-dialog' );
				dialog.id = `ensembles-event-detail-dialog-${newVal}`;
				document.body.appendChild( dialog );
			}

			fetchCollectionData( newVal ).then( ( collectionData ) => {
				if ( this.getAttribute( 'data-sort-order' ) === 'descending' ) {
					collectionData?.items.reverse();
				}

				this.#collectionData = collectionData;
			} ).finally( () => {
				this.#renderCollectionData();
			} );
		}
	}

	#renderCollectionData(): void {
		this.innerHTML = '';

		if ( !this.collectionSlug ) {
			return;
		}

		if ( !this.#collectionData ) {
			return;
		}

		// ARTWORK
		const collectionDetailListArtworks = document.createElement( 'div' );
		collectionDetailListArtworks.classList.add( 'collection-detail__list' );
		collectionDetailListArtworks.setAttribute( 'id', 'artworks' );

		const artworksInExpo = document.createElement( 'div' );
		artworksInExpo.classList.add( 'artworks-list' );

		for ( let index = 0; index < this.#collectionData.items.length; index++ ) {
			const element = this.#collectionData.items[index];
			const teaser = itemTeaser( this.collectionSlug, element );
			artworksInExpo.appendChild( teaser );
			if ( index >= NUMBER_OF_ARTWORKS_SHOWN_BY_DEFAULT ) {
				teaser.setAttribute( 'hidden', '' );
			}
			if ( index === NUMBER_OF_ARTWORKS_SHOWN_BY_DEFAULT + 1 ) {
				teaser.id = 'show-more';
			}
		}

		collectionDetailListArtworks.appendChild( this.#renderArtworksTitle() );
		collectionDetailListArtworks.appendChild( artworksInExpo );

		// SHOW MORE
		if ( this.#collectionData.items.length > NUMBER_OF_ARTWORKS_SHOWN_BY_DEFAULT ) {
			collectionDetailListArtworks.appendChild( renderShowMoreButton( this ) );
		}

		// STRUCTURE
		if ( this.#collectionData.items.length > 0 ) {
			this.appendChild( renderDivider() );
			this.appendChild( collectionDetailListArtworks );
		}
	}

	#renderArtworksTitle( ) {
		const artworksTitle = document.createElement( 'h2' );
		artworksTitle.classList.add( 'collection-detail__list__title' );

		artworksTitle.textContent = this.collectionTitle ?? translate(
			EN.of( 'Artworks in this collection' ),
			NL.of( 'Kunstwerken in deze collectie' ),
			FR.of( 'Œuvres dans cette collection' )
		);

		return artworksTitle;
	}
}

function renderDivider() {
	const divider = document.createElement( 'hr' );
	divider.classList.add( 'collection-detail__divider' );

	return divider;
}

function renderShowMoreButton( parent: MrEnsemblesCollectionDetailScreen ) {
	const showMoreContainer = document.createElement( 'div' );
	showMoreContainer.classList.add( 'artworks-list__show-more' );

	const showMoreButton = document.createElement( 'a' );
	showMoreButton.classList.add( 'artworks-list__show-more__button', 'button' );
	showMoreButton.innerHTML = translate(
		EN.of( 'Show more' ),
		NL.of( 'Toon meer' ),
		FR.of( 'Voir plus' )
	);
	showMoreButton.href = '#show-more';

	showMoreButton.addEventListener( 'click', () => {
		const hiddenItems = parent.querySelectorAll( '.artworks-list__item[hidden]' );
		for ( const item of hiddenItems ) {
			item.removeAttribute( 'hidden' );
		}

		showMoreContainer.setAttribute( 'hidden', '' );
	} );

	showMoreContainer.appendChild( showMoreButton );

	return showMoreContainer;
}

customElements.define( 'mr-ensembles-collection-detail-screen', MrEnsemblesCollectionDetailScreen );
